import React, { useState, useEffect, useContext } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Page, Text, StyleSheet, View, Image, Document } from '@react-pdf/renderer';
import HandoutPdfImageLoader from './handout-pdf-image-loader';
import PdfCallout from './pdf-callout';
import PdfIconStrip from './pdf-icon-strip';
import Inter from '../../../../assets/fonts/Inter-Variable.ttf';
import InterItalic from '../../../../assets/fonts/Inter-Italic.ttf';
import InterBold from '../../../../assets/fonts/Inter-Bold.ttf';
import InterExtraBold from '../../../../assets/fonts/Inter-ExtraBold.ttf';
import { Font, pdf } from '@react-pdf/renderer';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import { CircularProgress, Box, Typography } from '@mui/material';
import { toJS } from 'mobx';
import { CLIENT_NAME_LABEL, PATIENT_NAME_LABEL } from '../../../helper/patientLabels';

Font.register({ family: 'Inter', src: Inter, fontWeight: 'normal' });
Font.register({ family: 'Inter', src: Inter, fontWeight: '500' });
Font.register({ family: 'Inter', src: InterItalic, fontWeight: 'normal', fontStyle: 'italic' });
Font.register({ family: 'InterBold', src: InterBold, fontWeight: '600' });
Font.register({ family: 'InterExtraBold', src: InterExtraBold, fontWeight: '700' });
Font.registerHyphenationCallback((word) => {
    // remove - entirely so words move to next line without being split
    return [word];
});

const Footer = ({ clinicData, clinicLogoBase64 }) => {
    const logoSrc = clinicLogoBase64 || null;
    const hasLogo = !!logoSrc;
    const hasContactInfo = !!clinicData?.emailAddress || !!clinicData?.clinicNumber;
    const showContactBlock = !!clinicData?.clinicName && hasContactInfo;

    console.warn('[PDF Footer] Rendering with:', {
        hasLogo,
        hasContactInfo,
        showContactBlock,
        clinicName: clinicData?.clinicName,
        email: clinicData?.emailAddress,
        phone: clinicData?.clinicNumber,
    });

    const contactLine = showContactBlock ? buildContactLine(clinicData.emailAddress, clinicData.clinicNumber) : null;
    const copyrightLine = '@ Copyright Cetra Health. All rights reserved.';

    return (
        <View fixed style={styles.footerContainer}>
            {/* Contact Information */}
            {contactLine && <Text style={styles.contactText}>{contactLine}</Text>}

            {/* Copyright + logo on same row so they never overlap */}
            <View style={styles.footerBottom}>
                <Text style={styles.copyrightText}>{copyrightLine}</Text>
                {hasLogo && <Image src={logoSrc} style={styles.image} cache={false} />}
            </View>
        </View>
    );
};

const buildContactLine = (email, phone) => {
    let message = 'For any queries, please contact via';
    if (email) {
        message += ` ${email}`;
    }
    if (email && phone) {
        message += ' or';
    }
    if (phone) {
        message += ` ${phone}`;
    }
    return `${message}.`;
};

function HandoutPdf() {
    const { id: contentId } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const showLabels = queryParams.get('showLabels') === 'true';
    const customisedId = queryParams.get('customisedId');
    const whiteboardId = queryParams.get('whiteboardId');
    const patientName = queryParams.get('patientName');
    const patientSurname = queryParams.get('patientSurname');
    console.warn(
        'handout values...',
        contentId,
        showLabels,
        'customisedId:',
        customisedId,
        'whiteboardId:',
        whiteboardId,
        'patientName:',
        patientName,
        'patientSurname:',
        patientSurname,
    );
    const { authStore, contentStore, imageStore, userDataStore, customisedHandoutStore, whiteboardStore } =
        useContext(StoreContext);
    const [handoutData, setHandoutData] = useState(null);
    const [whiteboardData, setWhiteboardData] = useState(null);
    const [isCustomisedHandout, setIsCustomisedHandout] = useState(false);
    const [clinicData, setClinicData] = useState(null);
    const [clinicLogoBase64, setClinicLogoBase64] = useState(null);
    const [clinicLogoReady, setClinicLogoReady] = useState(false);
    console.warn('handoutData, ', { handoutData, authStore, imageStore });
    const [loadedImages, setLoadedImages] = useState(null);
    const locale = 'en-UK';
    const localeData = isCustomisedHandout
        ? handoutData?.effectiveContentData?.[locale]
        : handoutData?.contentData[locale];
    let mainIllustrationRendered = false;

    useEffect(() => {
        // Wait for stores to be initialized
        if (!contentStore) {
            console.warn('contentStore not yet initialized, waiting...');
            return;
        }

        console.warn(toJS(userDataStore));
        const fetchData = async () => {
            // Check if we need to load a customised handout
            if (customisedId && customisedHandoutStore) {
                console.warn(`Fetching customised handout: ${customisedId}`);
                try {
                    const customisedHandout = await customisedHandoutStore.getCustomisedHandoutById(customisedId);
                    if (customisedHandout) {
                        console.warn('fetchedData, this is the customised handout: ', customisedHandout);
                        setHandoutData(customisedHandout);
                        setIsCustomisedHandout(true);
                        return;
                    } else {
                        console.error('Customised handout not found:', customisedId);
                    }
                } catch (error) {
                    console.error('Failed to fetch customised handout:', error);
                }
            }

            // Check if we need to load a whiteboard handout
            if (whiteboardId && whiteboardStore) {
                console.warn(`Fetching whiteboard handout: ${whiteboardId}`);
                try {
                    const whiteboard = await whiteboardStore.getWhiteboardById(whiteboardId);
                    if (whiteboard) {
                        console.warn('fetchedData, this is the whiteboard: ', whiteboard);
                        setWhiteboardData(whiteboard);

                        // For whiteboards, we need to fetch the BASE content separately
                        // Whiteboards don't have effectiveContentData, only canvas modifications
                        console.warn(`Fetching base content for whiteboard, contentId: ${contentId}`);
                        const fetchedData = contentStore.getContentFromIds([contentId]);
                        if (fetchedData && fetchedData.length > 0) {
                            console.warn('fetchedData, base content for whiteboard: ', fetchedData);
                            setHandoutData(fetchedData[0]);
                            setIsCustomisedHandout(false); // Use base content data, not effectiveContentData
                        }
                        return;
                    } else {
                        console.error('Whiteboard handout not found:', whiteboardId);
                    }
                } catch (error) {
                    console.error('Failed to fetch whiteboard handout:', error);
                }
            }

            // Otherwise, fetch standard handout data
            console.warn(`Fetching content data for contentId: ${contentId}`);
            const fetchedData = contentStore.getContentFromIds([contentId]);
            if (fetchedData && fetchedData.length > 0) {
                console.warn('fetchedData, this is the handout: ', fetchedData);
                setHandoutData(fetchedData[0]);
                setIsCustomisedHandout(false);
            } else {
                console.warn('failed to fetch data');
            }
        };
        fetchData();
    }, [contentId, contentStore, customisedId, customisedHandoutStore, whiteboardId, whiteboardStore, userDataStore]);

    // Extract clinic data from userDataStore (no API call needed)
    useEffect(() => {
        if (!userDataStore?.userData) {
            console.warn('[PDF] No userDataStore available, using empty clinic data');
            setClinicData({});
            return;
        }

        // Extract clinic data from userDataStore.
        // clinicImageId is stored as a presigned URL — use it directly, do NOT prefix with API base URL.
        const extractedClinicData = {
            clinicName: userDataStore.userData.clinicName || '',
            emailAddress: userDataStore.userData.emailAddress || '',
            clinicNumber: userDataStore.userData.clinicNumber || '',
            url: userDataStore.userData.clinicImageId || null,
        };

        console.warn('[PDF] Clinic data extracted from userDataStore:', extractedClinicData);
        setClinicData(extractedClinicData);
    }, [userDataStore?.userData]);

    // Load clinic logo as base64 so react-pdf Image can render it reliably.
    // Depend on the full clinicData object: clinicData starts as null (not yet extracted),
    // then becomes {} or { url: '...' }. We must NOT mark ready while it is still null.
    useEffect(() => {
        if (clinicData === null) {
            // clinicData not yet extracted from userDataStore — wait
            return;
        }
        if (!clinicData.url) {
            setClinicLogoReady(true);
            return;
        }
        console.warn('[PDF] Fetching clinic logo as base64 from:', clinicData.url);
        fetch(clinicData.url)
            .then((res) => res.blob())
            .then(
                (blob) =>
                    new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onerror = reject;
                        reader.onload = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    }),
            )
            .then((dataUri) => {
                console.warn('[PDF] Clinic logo loaded as base64 successfully');
                setClinicLogoBase64(dataUri);
                setClinicLogoReady(true);
            })
            .catch((err) => {
                console.warn('[PDF] Failed to load clinic logo as base64, skipping logo:', err);
                setClinicLogoReady(true);
            });
    }, [clinicData]);

    useEffect(() => {
        if (handoutData && loadedImages && userDataStore && clinicLogoReady) {
            downloadPdfDocument();
        }
    }, [handoutData, loadedImages, userDataStore, clinicData, clinicLogoReady]);

    // Show loading message immediately while data is being fetched
    // Check contentStore as well to ensure we show loading from the very beginning
    if (!contentStore || !handoutData || !loadedImages || !clinicLogoReady) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column">
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Please wait until PDF file is created
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {!contentStore ? 'Initializing...' : 'Generating printable PDF...'}
                </Typography>
                {/* Hidden component that loads images in the background */}
                {handoutData && !loadedImages && contentStore && (
                    <div style={{ display: 'none' }}>
                        <HandoutPdfImageLoader
                            handoutData={handoutData}
                            whiteboardData={whiteboardData}
                            authStore={authStore}
                            imageStore={imageStore}
                            showLabels={showLabels}
                            isCustomisedHandout={isCustomisedHandout}
                            onImagesLoaded={(images) => {
                                setLoadedImages(images);
                            }}
                        />
                    </div>
                )}
            </Box>
        );
    }

    // console.warn('number of loadedImages: ', loadedImages.length);
    // console.warn('loadedImages: ', loadedImages);

    const downloadPdfDocument = async () => {
        if (!handoutData || !loadedImages) {
            console.error('Handout data is not available.');
            return;
        }

        // Extract patient details - prioritize URL params, then fall back to shared metadata
        const sharingMetadata = handoutData.sharingMetadata || {};
        // Use current date/time when PDF is generated (not stored timestamps)
        const generatedAt = Date.now();

        const fallbackFirstName = userDataStore?.userData?.firstName || null;
        const fallbackLastName = userDataStore?.userData?.lastName || null;

        const basePatientDetails = {
            patientName: patientName || handoutData.patientName || null,
            patientSurname: patientSurname || handoutData.patientSurname || null,
            updatedByFirstName:
                handoutData.updatedByFirstName || sharingMetadata.updatedByFirstName || fallbackFirstName || null,
            updatedByLastName:
                handoutData.updatedByLastName || sharingMetadata.updatedByLastName || fallbackLastName || null,
            lastUpdated: generatedAt,
        };

        const patientDetails = whiteboardData
            ? {
                  ...basePatientDetails,
                  patientName: patientName || whiteboardData.patientName || basePatientDetails.patientName,
                  patientSurname: patientSurname || whiteboardData.patientSurname || basePatientDetails.patientSurname,
                  updatedByFirstName: whiteboardData.updatedByFirstName || basePatientDetails.updatedByFirstName,
                  updatedByLastName: whiteboardData.updatedByLastName || basePatientDetails.updatedByLastName,
              }
            : isCustomisedHandout
              ? {
                    ...basePatientDetails,
                    patientName: patientName || handoutData.patientName || basePatientDetails.patientName,
                    patientSurname: patientSurname || handoutData.patientSurname || basePatientDetails.patientSurname,
                    updatedByFirstName: handoutData.updatedByFirstName || basePatientDetails.updatedByFirstName,
                    updatedByLastName: handoutData.updatedByLastName || basePatientDetails.updatedByLastName,
                }
              : basePatientDetails;

        // Show disclaimer if content was edited (has updatedBy details) and we have clinic name
        const hasEditorDetails = patientDetails?.updatedByFirstName || patientDetails?.updatedByLastName;
        const showDisclaimer = (isCustomisedHandout || whiteboardData) && hasEditorDetails && clinicData?.clinicName;
        const doctorName = hasEditorDetails
            ? `${patientDetails.updatedByFirstName || ''} ${patientDetails.updatedByLastName || ''}`.trim()
            : '';

        console.warn('[PDF] Disclaimer check:', {
            isCustomisedHandout,
            whiteboardData: !!whiteboardData,
            hasEditorDetails,
            hasClinicName: !!clinicData?.clinicName,
            showDisclaimer,
            doctorName,
            clinicData,
        });

        const doc = (
            <Document>
                <Page size="A4" style={styles.page}>
                    <View style={styles.contentContainer}>
                        {localeData.map((section, index) => generateSection(section, index, patientDetails))}

                        {/* Disclaimer for edited content */}
                        {showDisclaimer && (
                            <Text style={styles.disclaimer}>
                                This handout has been created by Cetra Health and edited by {doctorName} of{' '}
                                {clinicData.clinicName}. This information is for guidance only and does not represent
                                medical advice.
                            </Text>
                        )}
                    </View>
                    <Footer clinicData={clinicData} clinicLogoBase64={clinicLogoBase64} />
                </Page>
            </Document>
        );

        try {
            const blob = await pdf(doc).toBlob('application/pdf');
            const url = window.URL.createObjectURL(blob);

            fetch(url)
                .then((response) => response.blob())
                .then((blob) => {
                    const newBlobUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = newBlobUrl;

                    // Generate filename based on handout type
                    let filename;
                    if (isCustomisedHandout) {
                        // For customised handouts, extract document title from effectiveContentData
                        const titleBlock = localeData?.[0]?.blocks?.find((block) => block.type === 'documentTitle');
                        const documentTitle = titleBlock?.value || 'Customised Handout';
                        filename = `Cetra - ${documentTitle}.pdf`;
                    } else {
                        filename = `Cetra - ${handoutData.contentName}.pdf`;
                    }

                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();

                    // Send success message to parent window
                    if (window.opener && !window.opener.closed) {
                        window.opener.postMessage(
                            {
                                type: 'PDF_GENERATED',
                                fileName: filename,
                            },
                            window.location.origin,
                        );
                    }

                    // Clean up
                    window.URL.revokeObjectURL(url);
                    window.URL.revokeObjectURL(newBlobUrl);
                    a.remove();

                    // Keep the tab open briefly for debugging (e.g. to inspect variables)
                    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                    if (!isSafari) {
                        // leave that for future investigations
                        // setTimeout(() => {
                        window.close();
                        // }, 50000); // Close after 30s to allow investigation
                    }
                })
                .catch(console.error);
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    // This code should not be reached - the loading UI is handled in the early return above
    // If we reach here, PDF generation is in progress and will auto-close the window
    return null;

    function generateSection(jsonContent, index, patientDetails) {
        if (jsonContent.sectionName === 'printPageBreak') {
            // Return a View component with a break to insert a page break
            return <View key={`page-break-${index}`} break />;
        } else {
            const blocks =
                jsonContent.sectionName === 'diagram' ? [jsonContent.blocks[showLabels ? 0 : 1]] : jsonContent.blocks;

            return (
                <View key={jsonContent.sectionName + index}>
                    {blocks.map((block, blockIndex) =>
                        blockToComponentMapping(
                            block,
                            blockIndex,
                            jsonContent.sectionName,
                            patientDetails,
                            index === 0,
                        ),
                    )}
                </View>
            );
        }
    }

    function blockToComponentMapping(block, blockIndex, sectionName, patientDetails, isFirstSection) {
        switch (block.type) {
            case 'documentTitle':
                // Format date/time for "Given by" line
                const formatDateTime = (timestamp) => {
                    if (!timestamp) return '';
                    const date = new Date(timestamp);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    return `${day}/${month}/${year} ${hours}:${minutes}`;
                };

                const givenByName = [
                    patientDetails?.updatedByFirstName?.trim(),
                    patientDetails?.updatedByLastName?.trim(),
                ]
                    .filter(Boolean)
                    .join(' ');
                const hasPatientDetails =
                    patientDetails &&
                    (patientDetails.patientName ||
                        patientDetails.patientSurname ||
                        givenByName ||
                        patientDetails.lastUpdated);

                // Combine patient name and surname for Physio (same person)
                const fullPatientName = [patientDetails?.patientName, patientDetails?.patientSurname]
                    .filter(Boolean)
                    .join(' ');

                const patientDetailRows = hasPatientDetails
                    ? [
                          {
                              left: fullPatientName ? `Patient: ${fullPatientName}` : '',
                              right: '',
                          },
                          {
                              left: patientDetails.lastUpdated
                                  ? `Date: ${formatDateTime(patientDetails.lastUpdated)}`
                                  : '',
                              right: givenByName ? `Given by: ${givenByName}` : '',
                          },
                      ].filter((row) => row.left || row.right)
                    : [];

                return (
                    <React.Fragment key={`${sectionName}-${blockIndex}-title`}>
                        <Text style={styles.title}>{block.value}</Text>
                        {patientDetailRows.length > 0 && (
                            <View style={styles.patientDetailsWrapper}>
                                {patientDetailRows.map((row, idx) => (
                                    <View
                                        key={`${sectionName}-${blockIndex}-patient-${idx}`}
                                        style={styles.patientDetailsRow}
                                    >
                                        <Text style={styles.patientDetailsCell}>{row.left}</Text>
                                        <Text style={styles.patientDetailsCell}>{row.right}</Text>
                                    </View>
                                ))}
                            </View>
                        )}
                    </React.Fragment>
                );
            case 'sectionTitle':
                return (
                    <Text style={styles.sectionTitle} key={`${sectionName}-${blockIndex}-sectionTitle`}>
                        {block.value}
                    </Text>
                );
            case 'sectionSubtitle':
                return (
                    <Text style={styles.sectionSubtitle} key={`${sectionName}-${blockIndex}-sectionSubtitle`}>
                        {block.value}
                    </Text>
                );
            case 'calloutSmall':
                return <PdfCallout content={block.value} key={`${sectionName}-${blockIndex}-calloutSmall`} />;
            case 'calloutMedium':
                return <PdfCallout content={block.value} key={`${sectionName}-${blockIndex}-calloutMedium`} />;
            case 'paragraph':
                return (
                    <Text style={styles.paragraph} key={`${sectionName}-${blockIndex}-paragraph`}>
                        {block.value}
                    </Text>
                );
            case 'list':
                return block.values.map((item, itemIndex) => (
                    <Text
                        style={styles.paragraph}
                        key={`${sectionName}-${blockIndex}-list-${itemIndex}`}
                    >{`\u2022 ${item}`}</Text>
                ));
            case 'iconStrip':
                // console.warn('iconStrip: ', block.iconDetails);
                // Add page break before iconStrip in symptoms section to avoid squashing
                const shouldBreakBeforeIcons = sectionName === 'symptoms';
                return (
                    <React.Fragment key={`${sectionName}-${blockIndex}-iconStrip-wrapper`}>
                        {shouldBreakBeforeIcons && <View break={true}></View>}
                        <PdfIconStrip iconDetails={block.iconDetails} loadedImages={loadedImages} />
                    </React.Fragment>
                );
            case 'illustration':
                // For whiteboards or customised handouts with attached whiteboard in main illustration section, use whiteboard canvas image
                let imageUrl = block.src;
                const isMainIllustration = !mainIllustrationRendered;
                if (isMainIllustration) {
                    mainIllustrationRendered = true;
                }
                if (whiteboardData && whiteboardData.canvasImageLink && isMainIllustration) {
                    // For standalone whiteboards, use canvasImageLink
                    imageUrl = whiteboardData.canvasImageLink;
                } else if (isCustomisedHandout && handoutData.attachedWhiteboardCanvasImageLink && isMainIllustration) {
                    // For customised handouts with attached whiteboard, use attachedWhiteboardCanvasImageLink
                    imageUrl = handoutData.attachedWhiteboardCanvasImageLink;
                }

                const base64Image = loadedImages.find((img) => img.url.includes(imageUrl));
                // console.warn('loadedImages:', loadedImages);
                // console.warn(`base64Image: ${base64Image}`);
                if (base64Image)
                    return (
                        <Image
                            key={`${sectionName}-${blockIndex}-illustration`}
                            src={base64Image.data.data}
                            cache={false}
                            style={isMainIllustration ? styles.mainIllustrationImage : undefined}
                        />
                    );
                else return <Text>*** ILLUSTRATION NOT FOUND ***</Text>;
            case 'printPageBreak':
                return <View break={true}></View>;
            default:
                return null;
        }
    }
}

const styles = StyleSheet.create({
    footerContainer: {
        position: 'absolute',
        bottom: 12,
        left: 10,
        right: 10,
    },
    footerBottom: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    copyrightText: {
        fontSize: 8,
        fontFamily: 'Inter',
        marginTop: 0,
        flex: 1,
    },
    contactText: {
        fontSize: 8,
        fontFamily: 'Inter',
        marginBottom: 1,
        color: '#212121',
    },
    page: {
        fontFamily: 'Inter',
        backgroundColor: '#ffffff',
        color: '#212121',
        padding: 20,
        paddingBottom: 80,
    },
    contentContainer: {
        marginBottom: 80,
    },
    title: {
        fontSize: 24,
        marginBottom: 10,
        fontFamily: 'InterExtraBold',
        color: '#000453',
    },
    patientDetailsWrapper: {
        marginBottom: 20,
        marginTop: 8,
    },
    patientDetailsRow: {
        flexDirection: 'row',
        display: 'flex',
        marginBottom: 4,
    },
    patientDetailsCell: {
        fontSize: 12,
        color: '#000453',
        fontFamily: 'Inter',
        flex: 1,
    },
    disclaimer: {
        fontSize: 10,
        marginTop: 20,
        marginBottom: 10,
        color: '#666666',
        fontFamily: 'Inter',
        fontStyle: 'italic',
    },
    sectionTitle: {
        fontSize: 20,
        marginBottom: 10,
        fontFamily: 'InterBold',
        color: '#000453',
    },
    sectionSubtitle: {
        fontSize: 16,
        marginBottom: 10,
        fontFamily: 'InterBold',
        color: '#000453',
    },
    paragraph: {
        fontSize: 12,
        marginBottom: 10,
        fontFamily: 'Inter',
        color: '#1d2129', // grey[900]
    },
    mainIllustrationImage: {
        width: 480,
        alignSelf: 'center',
        marginVertical: 12,
    },
    image: {
        height: 50,
        maxWidth: 200,
        objectFit: 'contain',
        flexShrink: 0,
        marginLeft: 8,
    },
});

export default HandoutPdf;
